import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Client } from '@/types/shelter';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useRef, useCallback } from 'react';
import { logActivity } from '@/hooks/useActivityLog';
import { formatClientFieldList } from '@/lib/clientFieldLabels';
import { useOffline } from '@/contexts/OfflineContext';
import { getFromOfflineStore } from '@/lib/offlineDb';
import { createRealtimeChannelName, logRealtimeIssue, removeRealtimeChannel } from '@/lib/realtime';
import {
  ClientStatus,
  normalizeStatus,
  isValidTransition,
  canRoleSetStatus,
  getTransitionError,
  getTimestampUpdates,
} from '@/utils/statusTransitions';

// Helper: determine role bucket for transition checks
const getRoleBucket = async (): Promise<'admin' | 'staff'> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 'staff';
  const { data } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', user.id)
    .maybeSingle();
  if (!data) return 'staff';
  const adminRoles = ['super_admin', 'program_director', 'manager'];
  return adminRoles.includes(data.role) ? 'admin' : 'staff';
};

export const useClients = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isOnline, cacheData } = useOffline();

  // Debounced realtime invalidation — batch concurrent updates
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedInvalidate = useCallback((keys: string[][]) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      keys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
      debounceRef.current = null;
    }, 250);
  }, [queryClient]);

  useEffect(() => {
    try {
      const channel = supabase
        .channel(createRealtimeChannelName('clients-changes'))
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'clients' }, (payload) => {
          debouncedInvalidate([['clients']]);
          const newClient = payload.new as any;
          toast({ title: 'New Client', description: `${newClient.first_name} ${newClient.last_name} has been added` });
        })
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'clients' }, (payload) => {
          debouncedInvalidate([['clients'], ['checkins'], ['rooms'], ['client-belongings']]);
          const updated = payload.new as any;
          const old = payload.old as any;
          if (updated.status !== old.status) {
            toast({ title: 'Client Status Updated', description: `${updated.first_name} ${updated.last_name} is now ${updated.status}` });
          }
        })
        .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'clients' }, () => {
          debouncedInvalidate([['clients']]);
        })
        .subscribe((_status, error) => {
          if (error) logRealtimeIssue('Clients', error);
        });

      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        removeRealtimeChannel(channel);
      };
    } catch (error) {
      logRealtimeIssue('Clients', error);
    }
  }, [queryClient, toast, debouncedInvalidate]);

  const { data: dbClients = [], isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      if (!isOnline) {
        return getFromOfflineStore<Client>('clients');
      }

      // Use consolidated RPC — single query instead of 4
      const { data: rpcResult, error } = await supabase.rpc('get_clients_for_site', {
        p_site_id: null,
        p_limit: 500,
        p_offset: 0,
      });

      if (error) throw error;

      const rows = (rpcResult || []) as any[];

      const transformedClients = rows.map((client: any) => {
        const age = client.date_of_birth
          ? new Date().getFullYear() - new Date(client.date_of_birth).getFullYear()
          : 0;
        const rawStatus = client.status || 'unbedded';
        const status = normalizeStatus(rawStatus);
        const resolvedCheckInTime = client.check_in_time || client.fallback_checkin_time || (status === 'checked-in' ? (client.updated_at || client.created_at) : undefined);

        return {
          id: client.id,
          name: `${client.first_name} ${client.last_name}`.trim(),
          age,
          room: client.room_number || undefined,
          room_id: client.room_id,
          roomId: client.room_id,
          site_id: client.site_id,
          siteId: client.site_id,
          site_name: client.site_name || 'Unassigned',
          siteName: client.site_name || 'Unassigned',
          status: status as Client['status'],
          caseWorker: client.case_worker_name || 'Unassigned',
          case_worker_id: client.case_worker_id || undefined,
          alerts: Array.isArray(client.active_alerts) ? client.active_alerts : [],
          phone: client.phone,
          email: client.email,
          address: client.address,
          
          // allergies removed — no PHI
          checkInTime: resolvedCheckInTime,
          checkOutTime: client.check_out_time,
          lastActivity: client.check_out_time || resolvedCheckInTime,
          photo: client.photo_url,
          emergencyContact: client.emergency_contact_name ? {
            name: client.emergency_contact_name,
            relationship: client.emergency_contact_relationship || '',
            phone: client.emergency_contact_phone || ''
          } : undefined,
          isReturnee: client.is_returnee,
          returneeObservations: client.returnee_observations,
          passStart: client.pass_start,
          passEnd: client.pass_end,
          passType: client.pass_type,
          isHeadOfHousehold: client.is_head_of_household,
          partnerId: client.partner_id,
          partnerName: client.partner_name || undefined,
          children: client.children || [],
          propertyStorageNotes: client.property_storage_notes || undefined,
          exitReason: client.exit_reason || null,
          exit_reason: client.exit_reason || null,
          exitedAt: client.exited_at || null,
          exited_at: client.exited_at || null,
          unbedded_at: client.unbedded_at || null,
          reserved_at: client.reserved_at || null,
          waitlist_at: client.waitlist_at || null,
          not_present_at: client.not_present_at || null,
          created_at: client.created_at,
          updated_at: client.updated_at,
        };
      }) as Client[];

      await cacheData('clients', transformedClients);
      return transformedClients;
    },
    staleTime: isOnline ? 30000 : Infinity,
    refetchInterval: isOnline ? 15000 : false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });


  const clients = dbClients;

  // ── Status change with transition validation ──
  const changeStatus = useMutation({
    mutationFn: async ({
      id,
      newStatus,
      roomId,
      notes,
      propertyStorageNotes,
      statusReason,
      reservedUntil,
    }: {
      id: string;
      newStatus: ClientStatus;
      roomId?: string;
      notes?: string;
      propertyStorageNotes?: string;
      statusReason?: string;
      reservedUntil?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const roleBucket = await getRoleBucket();

      // Fetch current status
      const { data: current, error: fetchError } = await supabase
        .from('clients')
        .select('status, site_id')
        .eq('id', id)
        .single();
      if (fetchError) throw fetchError;

      const currentStatus = normalizeStatus(current.status || 'unbedded');
      const transitionError = getTransitionError(currentStatus, newStatus, roleBucket);
      if (transitionError) throw new Error(transitionError);

      // Guard: block unbedded if client has active pass
      if (newStatus === 'unbedded') {
        const { data: passData } = await supabase
          .from('clients')
          .select('pass_end')
          .eq('id', id)
          .single();
        const { getUnbedGuardError } = await import('@/utils/statusTransitions');
        const unbedError = getUnbedGuardError(passData?.pass_end);
        if (unbedError) throw new Error(unbedError);
      }

      // Build update payload
      const timestamps = getTimestampUpdates(newStatus);
      const updateData: any = {
        status: newStatus,
        ...timestamps,
      };

      if (propertyStorageNotes !== undefined) updateData.property_storage_notes = propertyStorageNotes;
      if (statusReason !== undefined) updateData.status_reason = statusReason;
      if (reservedUntil !== undefined) updateData.reserved_until = reservedUntil;

      // Guard: check-in requires a room
      if (newStatus === 'checked-in' && !roomId) {
        const { data: clientRoom } = await supabase
          .from('clients')
          .select('room_id')
          .eq('id', id)
          .single();
        if (!clientRoom?.room_id) {
          throw new Error('Client must be assigned a room before checking in');
        }
      }

      // Handle room assignment for check-in
      let siteId = current.site_id;
      if (newStatus === 'checked-in' && roomId) {
        updateData.room_id = roomId;
        const { data: roomData } = await supabase.from('rooms').select('site_id').eq('id', roomId).single();
        if (roomData) {
          siteId = roomData.site_id;
          updateData.site_id = siteId;
        }
      }

      // Only clear room when explicitly unbedded
      if (newStatus === 'unbedded') {
        updateData.room_id = null;
      }

      const { data, error } = await supabase
        .from('clients')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      // ── Checkins table consistency ──
      if (newStatus === 'checked-in') {
        const nowIso = new Date().toISOString();

        // Auto-log "Property Returned" when client transitions from unbedded back to checked-in
        if (currentStatus === 'unbedded') {
          await supabase.from('notes').insert({
            title: 'Property Returned',
            content: 'Stored belongings returned to client upon re-bedding.',
            client_id: id,
            site_id: siteId,
            created_by: user.id,
            category: 'property_return',
            note_type: 'general',
          });
        }

        // Close any existing open checkin first
        await supabase
          .from('checkins')
          .update({ checked_out_at: nowIso })
          .eq('client_id', id)
          .is('checked_out_at', null);

        // Insert new checkin
        await supabase.from('checkins').insert({
          client_id: id,
          site_id: siteId,
          staff_id: user.id,
          checked_in_at: nowIso,
          notes: notes || null,
        });

        const { data: resolvedPassAlerts } = await supabase
          .from('alerts')
          .update({ status: 'resolved', acknowledged_by: user.id, updated_at: nowIso })
          .eq('client_id', id)
          .eq('status', 'active')
          .ilike('title', 'Pass Expired:%')
          .select('id, title');

        if (resolvedPassAlerts && resolvedPassAlerts.length > 0) {
          await logActivity({
            actionType: 'acknowledge_alert',
            actionDetails: {
              client_id: id,
              alert_titles: resolvedPassAlerts.map((a) => a.title),
              reason: 'Client checked back in after expired pass',
            },
            siteId,
          });
        }

        // ── Returnee detection: prior departure >3 days ago ──
        try {
          const { data: priorLogs } = await supabase
            .from('user_activity_logs')
            .select('action_type, action_details, created_at')
            .in('action_type', ['client_status_change', 'archive_client'])
            .lt('created_at', nowIso)
            .order('created_at', { ascending: false })
            .limit(500);
          let lastDepartureAt: string | null = null;
          let lastDepartureKind: string | null = null;
          for (const log of (priorLogs || []) as any[]) {
            const d = log.action_details || {};
            if (d.client_id !== id) continue;
            const to = d.to ?? d.new_status;
            if (log.action_type === 'archive_client' || to === 'checked-out' || to === 'unbedded' || to === 'not_present') {
              lastDepartureAt = log.created_at;
              lastDepartureKind = log.action_type === 'archive_client' ? 'archived' : to;
              break;
            }
          }
          if (lastDepartureAt) {
            const daysAway = Math.floor((new Date(nowIso).getTime() - new Date(lastDepartureAt).getTime()) / 86400000);
            if (daysAway > 3) {
              const { data: clientRow } = await supabase
                .from('clients')
                .select('first_name, last_name')
                .eq('id', id)
                .single();
              const clientName = clientRow ? `${clientRow.first_name} ${clientRow.last_name}`.trim() : 'Client';
              const returnDateStr = new Date(nowIso).toLocaleDateString();
              const departDateStr = new Date(lastDepartureAt).toLocaleDateString();

              // Insert timeline note
              await supabase.from('notes').insert({
                title: `Returned after ${daysAway} days away`,
                content: `Client returned on ${returnDateStr} after ${daysAway} days away. Previous departure: ${departDateStr} (${lastDepartureKind}).`,
                client_id: id,
                site_id: siteId,
                created_by: user.id,
                category: 'returnee',
                note_type: 'general',
              });

              // Dedup active returnee alert before inserting
              const { data: existing } = await supabase
                .from('alerts')
                .select('id')
                .eq('client_id', id)
                .eq('type', 'returnee')
                .eq('status', 'active')
                .limit(1);
              if (!existing || existing.length === 0) {
                await supabase.from('alerts').insert({
                  title: `Returnee: ${clientName}`,
                  description: `Returned after ${daysAway} days. Confirm intake details and update plan.`,
                  type: 'info',
                  category: 'client',
                  client_id: id,
                  site_id: siteId,
                  created_by: user.id,
                  status: 'active',
                });
              }
            }
          }
        } catch (e) {
          console.warn('Returnee detection failed', e);
        }
      }

      if (newStatus === 'checked-out' || (newStatus === 'not_present' && currentStatus === 'checked-in')) {
        // Close the open checkin
        const { data: openCheckins } = await supabase
          .from('checkins')
          .select('id')
          .eq('client_id', id)
          .is('checked_out_at', null)
          .order('checked_in_at', { ascending: false })
          .limit(1);

        if (openCheckins && openCheckins.length > 0) {
          await supabase
            .from('checkins')
            .update({ checked_out_at: new Date().toISOString() })
            .eq('id', openCheckins[0].id);
        }
      }

      await logActivity({
        actionType: newStatus === 'checked-in' ? 'check_in' : newStatus === 'checked-out' ? 'check_out' : 'update_client',
        actionDetails: { client_id: id, from_status: currentStatus, to_status: newStatus, notes },
        siteId: siteId,
      });

      // Create alerts for unbedded/waitlist status changes
      if (newStatus === 'unbedded') {
        const clientRow = await supabase.from('clients').select('first_name, last_name').eq('id', id).single();
        const clientName = clientRow.data ? `${clientRow.data.first_name} ${clientRow.data.last_name}` : 'Unknown';
        await supabase.from('alerts').insert({
          title: `Client Unbedded: ${clientName}`,
          description: statusReason || `${clientName} has been unbedded`,
          type: 'warning',
          category: 'client',
          client_id: id,
          site_id: siteId,
          created_by: user.id,
          status: 'active'
        });

        // Auto-create "Unbedded Belongings" note if property storage notes provided
        if (propertyStorageNotes) {
          await supabase.from('notes').insert({
            title: 'Unbedded Belongings',
            content: propertyStorageNotes,
            client_id: id,
            site_id: siteId,
            created_by: user.id,
            category: 'property',
            note_type: 'general',
          });
        }
      }

      if (newStatus === 'waitlist') {
        const clientRow = await supabase.from('clients').select('first_name, last_name').eq('id', id).single();
        const clientName = clientRow.data ? `${clientRow.data.first_name} ${clientRow.data.last_name}` : 'Unknown';
        await supabase.from('alerts').insert({
          title: `Client Added to Waitlist: ${clientName}`,
          description: `${clientName} has been placed on the waitlist`,
          type: 'info',
          category: 'client',
          client_id: id,
          site_id: siteId,
          created_by: user.id,
          status: 'active'
        });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      queryClient.invalidateQueries({ queryKey: ['checkins'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Status Change Blocked', description: error.message || 'Failed to change status' });
    },
  });

  // ── Create client ──
  const createClient = useMutation({
    mutationFn: async (clientData: Partial<Client> & { siteId?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const nameParts = (clientData.name || '').split(' ');
      const firstName = nameParts[0] || 'Unknown';
      const lastName = nameParts.slice(1).join(' ') || '';

      const { data, error } = await supabase
        .from('clients')
        .insert([{
          first_name: firstName,
          last_name: lastName,
          date_of_birth: clientData.age ? new Date(new Date().getFullYear() - clientData.age, 0, 1).toISOString().split('T')[0] : null,
          phone: clientData.phone,
          email: clientData.email,
          address: clientData.address,
          // allergies removed — no PHI
          emergency_contact_name: clientData.emergencyContact?.name,
          emergency_contact_phone: clientData.emergencyContact?.phone,
          emergency_contact_relationship: clientData.emergencyContact?.relationship,
          status: clientData.status || 'unbedded',
          room_id: (clientData as any).roomId || null,
          site_id: clientData.siteId || null,
          case_worker_id: clientData.caseWorker && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clientData.caseWorker) ? clientData.caseWorker : null,
          photo_url: clientData.photo || null,
          is_returnee: clientData.isReturnee || false,
          returnee_observations: clientData.returneeObservations,
          gender: (clientData as any).gender,
          intake_date: (clientData as any).intake_date,
          name_suffix: (clientData as any).name_suffix ?? null,
          duplicate_override_reason: (clientData as any).duplicate_override_reason ?? null,
        }])
        .select()
        .single();

      if (error) throw error;

      await logActivity({
        actionType: 'create_client',
        actionDetails: { client_id: data.id, name: `${firstName} ${lastName}`, timestamp: new Date().toISOString() },
        siteId: clientData.siteId
      });

      // Auto-create "Reminder: New Intake" alert
      await supabase.from('alerts').insert({
        title: 'Reminder: New Intake',
        description: `New intake for ${firstName} ${lastName}`,
        type: 'warning',
        category: 'client',
        client_id: data.id,
        site_id: clientData.siteId || null,
        created_by: user.id,
        status: 'active'
      });

      const reminderAlerts = ((clientData as any).reminders || [])
        .filter((reminder: any) => reminder.type !== 'appointment' && reminder.type !== 'medication')
        .map((reminder: any) => {
          const detail = String(reminder.description || '').trim();
          const dueText = reminder.date ? new Date(reminder.date).toLocaleString() : 'Not scheduled';
          const typeLabels: Record<string, string> = {
            document: 'Document Reminder',
            followup: 'Follow-up Reminder',
          };
          return {
            title: typeLabels[reminder.type] || 'Reminder',
            description: `Reminder: ${detail}\nDue: ${dueText}\nClient: ${firstName} ${lastName}`,
            type: 'warning',
            category: 'reminder',
            client_id: data.id,
            site_id: clientData.siteId || null,
            created_by: user.id,
            status: 'active',
          };
        });


      if (reminderAlerts.length > 0) {
        await supabase.from('alerts').insert(reminderAlerts);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({ title: 'Success', description: 'Client created successfully' });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to create client' });
    }
  });

  // ── Update client (non-status fields) ──
  const updateClient = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Client> & { id: string; siteId?: string }) => {
      const updateData: any = {};

      // Fetch current data BEFORE building updateData so we can avoid
      // clobbering real DOBs when only age (derived) is passed through.
      const { data: currentClient } = await supabase.from('clients').select('*').eq('id', id).single();

      if (updates.name) {
        const nameParts = updates.name.split(' ');
        updateData.first_name = nameParts[0];
        updateData.last_name = nameParts.slice(1).join(' ') || nameParts[0];
      }

      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (
        updates.age !== undefined &&
        updates.age !== null &&
        !Number.isNaN(updates.age as number) &&
        updates.status === undefined // never re-derive DOB during a status change
      ) {
        // Only rewrite date_of_birth if the incoming age actually differs from
        // the age derived from the stored DOB. Otherwise unrelated saves (e.g.
        // room change) would overwrite a real DOB with `Jan 1` and log a
        // phantom "Profile Updated (Date of Birth)" entry.
        const storedDob = (currentClient as any)?.date_of_birth as string | null | undefined;
        const storedAge = storedDob
          ? Math.floor((Date.now() - new Date(storedDob).getTime()) / 31557600000)
          : null;
        if (storedAge === null || storedAge !== Number(updates.age)) {
          const year = new Date().getFullYear() - Number(updates.age);
          updateData.date_of_birth = `${year}-01-01`;
        }
      }

      if (updates.email !== undefined) updateData.email = updates.email;
      if (updates.address !== undefined) updateData.address = updates.address;
      
      if (updates.photo !== undefined) updateData.photo_url = updates.photo;
      // allergies removed — no PHI

      const nextSiteId = updates.siteId ?? (updates as any).site_id;
      if (nextSiteId !== undefined) updateData.site_id = nextSiteId || null;
      if (updates.room !== undefined) {
        if (updates.room && updates.room !== 'Unassigned') {
          const { data: roomData } = await supabase
            .from('rooms')
            .select('id')
            .eq('room_number', updates.room)
            .maybeSingle();
          updateData.room_id = roomData?.id || null;
        } else {
          updateData.room_id = null;
        }
      }
      if (updates.emergencyContact) {
        updateData.emergency_contact_name = updates.emergencyContact.name;
        updateData.emergency_contact_phone = updates.emergencyContact.phone;
        updateData.emergency_contact_relationship = updates.emergencyContact.relationship;
      }
      if ((updates as any).gender !== undefined) updateData.gender = (updates as any).gender;
      if ((updates as any).intake_date !== undefined) updateData.intake_date = (updates as any).intake_date;
      if ((updates as any).archived_at !== undefined) updateData.archived_at = (updates as any).archived_at;
      if ((updates as any).case_worker_id !== undefined) updateData.case_worker_id = (updates as any).case_worker_id || null;

      if (updates.isHeadOfHousehold !== undefined) updateData.is_head_of_household = updates.isHeadOfHousehold;
      if (updates.children !== undefined) updateData.children = updates.children;
      if (updates.partnerId !== undefined) updateData.partner_id = updates.partnerId || null;
      if (updates.partnerName !== undefined) updateData.partner_name = updates.partnerName || null;
      if (updates.propertyStorageNotes !== undefined) updateData.property_storage_notes = updates.propertyStorageNotes || null;

      // Allow status updates through updateClient for backward compat (no transition check)
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.checkInTime !== undefined) updateData.check_in_time = updates.checkInTime;
      if (updates.checkOutTime !== undefined) updateData.check_out_time = updates.checkOutTime;
      if (updates.status === 'checked-in') {
        updateData.pass_start = null;
        updateData.pass_end = null;
        updateData.pass_type = null;
      }
      if (updates.passStart !== undefined) updateData.pass_start = updates.passStart || null;
      if (updates.passEnd !== undefined) updateData.pass_end = updates.passEnd || null;
      if (updates.passType !== undefined) updateData.pass_type = updates.passType || null;

      // currentClient was already fetched above for DOB diff-guarding

      const { data, error } = await supabase
        .from('clients')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (updates.status === 'checked-in') {
        const { data: { user } } = await supabase.auth.getUser();
        await supabase
          .from('alerts')
          .update({ status: 'resolved', acknowledged_by: user?.id || null, updated_at: new Date().toISOString() })
          .eq('client_id', id)
          .eq('status', 'active')
          .ilike('title', 'Pass Expired:%');
      }

      const actuallyChanged: string[] = [];
      // When status is changing, exclude bookkeeping columns that ride along
      // with the transition — the status-change log already covers those.
      const statusSideEffectFields = updates.status !== undefined
        ? new Set(['status', 'check_in_time', 'check_out_time', 'pass_start', 'pass_end', 'pass_type', 'exit_reason'])
        : new Set<string>();
      if (currentClient) {
        for (const key of Object.keys(updateData)) {
          if (statusSideEffectFields.has(key)) continue;
          const oldVal = (currentClient as any)[key];
          const newVal = updateData[key];
          const oldStr = JSON.stringify(oldVal ?? null);
          const newStr = JSON.stringify(newVal ?? null);
          if (oldStr !== newStr) actuallyChanged.push(key);
        }
      } else {
        for (const key of Object.keys(updateData)) {
          if (statusSideEffectFields.has(key)) continue;
          actuallyChanged.push(key);
        }
      }


      const changedFields = formatClientFieldList(actuallyChanged);
      const clientName = currentClient
        ? `${(currentClient as any).first_name ?? ''} ${(currentClient as any).last_name ?? ''}`.trim()
        : undefined;

      if (changedFields.length > 0) {
        await logActivity({
          actionType: 'update_client',
          actionDetails: {
            client_id: id,
            client_name: clientName,
            updates: changedFields,
            changed_fields: changedFields,
          },
          siteId: updates.siteId
        });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({ title: 'Success', description: 'Client updated successfully' });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to update client' });
    }
  });

  // ── Archive ──
  const archiveClient = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('clients')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      await logActivity({ actionType: 'archive_client', actionDetails: { client_id: id, client_name: `${data.first_name} ${data.last_name}` } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({ title: 'Success', description: 'Client archived successfully' });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to archive client' });
    }
  });

  // ── Delete ──
  const deleteClient = useMutation({
    mutationFn: async (id: string) => {
      const { data: clientData } = await supabase
        .from('clients')
        .select('first_name, last_name, site_id')
        .eq('id', id)
        .single();

      await supabase.from('notes').delete().eq('client_id', id);
      await supabase.from('service_logs').delete().eq('client_id', id);
      await supabase.from('family_member_checkins').delete().eq('client_id', id);

      const { error } = await supabase.from('clients').delete().eq('id', id);
      if (error) throw error;

      await logActivity({
        actionType: 'delete_client',
        actionDetails: {
          client_id: id,
          client_name: clientData ? `${clientData.first_name} ${clientData.last_name}` : 'Unknown'
        },
        siteId: clientData?.site_id
      });

      return { id, name: clientData ? `${clientData.first_name} ${clientData.last_name}` : 'Unknown' };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      toast({ title: 'Client Deleted', description: `${data.name} has been permanently deleted` });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to delete client' });
    }
  });

  // ── Archived clients query ──
  const { data: archivedClients = [], isLoading: archivedLoading } = useQuery({
    queryKey: ['clients', 'archived'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select(`*, rooms:room_id (id, room_number), sites:site_id (id, name)`)
        .not('archived_at', 'is', null)
        .order('archived_at', { ascending: false });

      if (error) throw error;

      return data.map((client: any) => ({
        id: client.id,
        name: `${client.first_name} ${client.last_name}`.trim(),
        status: client.status,
        status_reason: client.status_reason,
        archived_at: client.archived_at,
        room: (client.rooms as any)?.room_number || undefined,
        site_name: (client.sites as any)?.name || 'Unassigned',
        site_id: client.site_id,
      }));
    },
    staleTime: 60000,
  });

  // ── Restore archived client ──
  const restoreClient = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('clients')
        .update({ archived_at: null, status: 'unbedded', status_reason: null })
        .eq('id', id)
        .select('first_name, last_name')
        .single();
      if (error) throw error;
      await logActivity({
        actionType: 'update_client',
        actionDetails: { client_id: id, action: 'restored', client_name: `${data.first_name} ${data.last_name}` },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({ title: 'Success', description: 'Client restored successfully' });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to restore client' });
    },
  });

  return {
    clients,
    isLoading,
    createClient: createClient.mutateAsync,
    updateClient: updateClient.mutateAsync,
    changeStatus: changeStatus.mutateAsync,
    isChangingStatus: changeStatus.isPending,
    archiveClient: archiveClient.mutateAsync,
    isDeleting: archiveClient.isPending,
    archivedClients,
    archivedLoading,
    restoreClient: restoreClient.mutateAsync,
  };
};
